/**
 * ReviewQueueList — security and quality review jobs grouped by daemon, with retry and run actions. Demo page: /email
 * (inbox groups with row actions).
 */
import { Play, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button, Card, EmptyState } from '../../../components/ui/index.js';
import type { DaemonReviewQueueItem } from './catalog-types.js';
import { groupQueueByDaemon } from './catalog-helpers.js';
import { ReviewQueueRow } from './ReviewQueueRow.js';

export function ReviewQueueList({
  items,
  onOpenCandidate,
  onEnableDaemon,
  onRetryItem,
  onRetryDaemon,
  onProcessDaemon,
  busy,
}: {
  items: DaemonReviewQueueItem[];
  onOpenCandidate: (candidateId: number) => void;
  onEnableDaemon: (daemonId: number, daemonName: string) => void;
  onRetryItem: (itemId: number) => void;
  onRetryDaemon: (daemonName: string) => void;
  onProcessDaemon: (daemonName: string) => void;
  busy: boolean;
}) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="No queued reviews"
        description="Security and quality daemon review tasks are clear."
      />
    );
  }
  return (
    <div className="space-y-5">
      {groupQueueByDaemon(items).map(({ daemonName, items: daemonItems }) => {
        const blocked = daemonItems.filter((item) => item.status === 'blocked').length;
        const failed = daemonItems.filter((item) => item.status === 'failed').length;
        const pending = daemonItems.filter((item) => item.status === 'pending').length;
        return (
          <Card
            key={daemonName}
            title={daemonName}
            actions={
              <>
                {failed > 0 && (
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => onRetryDaemon(daemonName)}>
                    <RefreshCw size={14} aria-hidden="true" /> Retry failed
                  </Button>
                )}
                {pending > 0 && (
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => onProcessDaemon(daemonName)}>
                    <Play size={14} aria-hidden="true" /> Run pending
                  </Button>
                )}
              </>
            }
          >
            <p className="-mt-3 mb-4 text-xs text-muted">
              {blocked} blocked, {failed} failed, {pending} pending
            </p>
            <div className="-mx-5 -mb-5 divide-y border-t">
              {daemonItems.map((item) => (
                <ReviewQueueRow
                  key={item.id}
                  item={item}
                  onOpenCandidate={onOpenCandidate}
                  onEnableDaemon={onEnableDaemon}
                  onRetryItem={onRetryItem}
                  onProcessDaemon={onProcessDaemon}
                  busy={busy}
                />
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
